import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { ParentPicker, type ParentType } from '../hierarchy/ParentPicker'
import { Button } from '../ui/Button'
import { Textarea } from '../ui/Textarea'
import { HabitService, type Habit, type CreateHabitRequest, type Weekday, type TimeWindow } from '../../services/habitService'
import { LIFE_AREA_OPTIONS, type Choice } from '../../utils/taskForm'
import { DURATION_CHIPS } from '../../utils/habitOptions'
import { SCHEDULE_MODE_OPTIONS, WEEKDAY_CHIPS, quickSpec } from '../../utils/habitForm'
import { hasSchedule } from '../../utils/habitSections'
import { buildRRule, deriveLegacyCadence, deriveLegacyPreferredDays, deriveLegacyTimeWindow } from '../../utils/rrule'

/**
 * "Fill the gaps" for a habit (owner 2026-09-06: the habit page's rail should
 * carry the same order as the task page's). One question at a time, as a
 * carousel — Next/Back move, answering closes the gap, nothing is put away.
 * The gaps are the facts the scheduler and the motivator need and the person
 * has not said yet: when it comes round, how long, the bare minimum, the cue,
 * why it matters, what it belongs to. Answering writes the real field.
 */
interface Props {
  habit: Habit
  onChanged: (habit: Habit) => void
  embedded?: boolean
}

type GapKind = 'schedule' | 'duration' | 'minimum' | 'cue' | 'why' | 'belongs'
interface Gap { kind: GapKind; question: string; hint: string }

function gapsOf(h: Habit): Gap[] {
  const out: Gap[] = []
  if (!hasSchedule(h)) out.push({ kind: 'schedule', question: 'When does it come round?', hint: 'helps me plan it' })
  if (!h.duration_minutes) out.push({ kind: 'duration', question: 'How long does one time take?', hint: 'helps me plan it' })
  if (!h.parent?.title && !h.parent_goal_id && !h.parent_skill_id && !(h.life_areas && h.life_areas.length) && !h.life_area) out.push({ kind: 'belongs', question: 'What is it for?', hint: 'helps me plan it' })
  if (!h.minimum_viable_repetition) out.push({ kind: 'minimum', question: 'On a rough day, what is the bare minimum that still counts?', hint: 'helps me keep you going' })
  if (!h.cue_or_anchor) out.push({ kind: 'cue', question: 'What does it follow — what is the cue?', hint: 'helps me keep you going' })
  if (!h.description) out.push({ kind: 'why', question: 'Why does it matter to you?', hint: 'helps me keep you going' })
  return out
}

function Chips({ options, value, onChange, label }: { options: Choice[]; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value === o.value
        return (
          <button key={o.value} type="button" aria-pressed={on} onClick={() => onChange(on ? '' : o.value)}
                  className={`min-h-touch rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${on ? 'border-brand bg-brand text-on-brand' : 'border-strong bg-surface text-secondary hover:border-brand'}`}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

const todayLocalISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }

export function HabitGapsCard({ habit, onChanged, embedded = false }: Props) {
  const gaps = useMemo(() => gapsOf(habit), [habit])
  const [idx, setIdx] = useState(0)
  const [answer, setAnswer] = useState<Record<string, string>>({})
  const [days, setDays] = useState<string[]>([])
  const [parent, setParent] = useState<{ parent_type: ParentType | ''; parent_id: number | null }>({ parent_type: '', parent_id: null })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (idx > gaps.length - 1) setIdx(Math.max(0, gaps.length - 1)) }, [gaps.length, idx])
  useEffect(() => { setAnswer({}); setDays([]); setErr(null) }, [idx])
  const gap = gaps[idx] ?? null
  const go = (d: number) => { if (gaps.length) setIdx((i) => (i + d + gaps.length) % gaps.length) }

  const save = async (payload: Partial<CreateHabitRequest> & Record<string, unknown>) => {
    setBusy(true); setErr(null)
    try {
      const svc = new HabitService()
      await svc.updateHabit(String(habit.id), payload)
      onChanged(await svc.getHabit(String(habit.id)))
      setAnswer({}); setDays([]); setParent({ parent_type: '', parent_id: null })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    if (!gap) return
    let payload: (Partial<CreateHabitRequest> & Record<string, unknown>) | null = null
    switch (gap.kind) {
      case 'schedule': {
        const spec = quickSpec({ schedule_mode: answer.mode || '', schedule_days: days })
        const time = (answer.time || '').trim()
        if (spec) {
          payload = {
            rrule: buildRRule(spec), scheduled_times_local: time ? [time] : [], schedule_start_date: todayLocalISO(),
            target_cadence: deriveLegacyCadence(spec), preferred_days: deriveLegacyPreferredDays(spec) as Weekday[],
            preferred_time_window: time ? (deriveLegacyTimeWindow(time) as TimeWindow) : undefined,
          }
        } else if (time) payload = { preferred_start_time: time }
        break
      }
      case 'duration': { const n = Number(answer.duration); if (n > 0) payload = { duration_minutes: n }; break }
      case 'belongs': {
        // Skills hold habits too (the ratified tree; prod 2026-09-12: three of eight sit under a skill).
        if (parent.parent_type === 'goal' && parent.parent_id) payload = { parent_goal_id: parent.parent_id }
        else if (parent.parent_type === 'skill' && parent.parent_id) payload = { parent_skill_id: parent.parent_id }
        else if (answer.life_area) payload = { life_areas: [answer.life_area] }
        break
      }
      case 'minimum': { const t = (answer.text || '').trim(); if (t) payload = { minimum_viable_repetition: t }; break }
      case 'cue': { const t = (answer.text || '').trim(); if (t) payload = { cue_or_anchor: t }; break }
      case 'why': { const t = (answer.text || '').trim(); if (t) payload = { description: t }; break }
    }
    if (!payload) { setErr('Pick or type an answer first.'); return }
    await save(payload)
  }

  return (
    <div className={embedded ? '' : 'rounded-xl border border-brand-subtle bg-surface p-4 sm:p-5'} data-testid="habit-gaps-card">
      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-primary">{gap ? 'Fill the gaps' : 'Nothing missing'}</div>
          {/* No count once nothing is missing: "6 of 6 said" answered no question (rule 7). */}
          <div className="text-xs text-muted">{gap ? `${gaps.length} detail${gaps.length === 1 ? '' : 's'} missing` : 'Everything the plan needs is here.'}</div>
        </div>
        {gaps.length > 1 && (
          <div className="ml-auto flex items-center gap-1 text-app-micro text-muted" data-testid="habit-gaps-nav">
            <button type="button" onClick={() => go(-1)} aria-label="Previous question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronLeft className="h-4 w-4" /></button>
            <span className="tabular-nums">{idx + 1} of {gaps.length}</span>
            <button type="button" onClick={() => go(1)} aria-label="Next question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {gap && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-primary" data-testid="habit-gaps-question">{gap.question}</p>

          {gap.kind === 'schedule' && (
            <div className="space-y-2">
              <Chips label="How often" options={SCHEDULE_MODE_OPTIONS} value={answer.mode || ''} onChange={(v) => setAnswer({ ...answer, mode: v })} />
              {answer.mode === 'days' && (
                <div className="flex flex-wrap gap-2" role="group" aria-label="Which days">
                  {WEEKDAY_CHIPS.map((d) => {
                    const on = days.includes(d.value)
                    return <button key={d.value} type="button" aria-pressed={on} onClick={() => setDays(on ? days.filter((x) => x !== d.value) : [...days, d.value])} className={`min-h-touch rounded-full border px-3.5 py-2 text-sm font-medium ${on ? 'border-brand bg-brand text-on-brand' : 'border-strong bg-surface text-secondary hover:border-brand'}`}>{d.label}</button>
                  })}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-secondary">
                <span>At</span>
                <input type="time" step={60} value={answer.time || ''} onChange={(e) => setAnswer({ ...answer, time: e.target.value })} aria-label="Time of day" className="min-h-touch rounded-md border border-strong bg-surface px-2 text-sm text-primary" />
              </label>
            </div>
          )}
          {gap.kind === 'duration' && <Chips label="How long" options={DURATION_CHIPS} value={answer.duration || ''} onChange={(v) => setAnswer({ ...answer, duration: v })} />}
          {gap.kind === 'belongs' && (
            <div className="space-y-3">
              <ParentPicker allowedTypes={['goal', 'skill']} parentType={parent.parent_type} parentId={parent.parent_id}
                            onChange={(f) => setParent({ parent_type: (f.parent_type as ParentType) || '', parent_id: f.parent_id ? Number(f.parent_id) : null })} />
              {!parent.parent_id && (
                <div>
                  <div className="mb-1.5 text-xs text-muted">Or, if it is just for you — which part of life?</div>
                  <Chips label="Life area" options={LIFE_AREA_OPTIONS} value={answer.life_area || ''} onChange={(v) => setAnswer({ ...answer, life_area: v })} />
                </div>
              )}
            </div>
          )}
          {(gap.kind === 'minimum' || gap.kind === 'cue' || gap.kind === 'why') && (
            <Textarea rows={2} value={answer.text ?? ''} onChange={(e) => setAnswer({ ...answer, text: e.target.value })}
                      placeholder={gap.kind === 'minimum' ? 'e.g. one page, five minutes' : gap.kind === 'cue' ? 'e.g. after the first coffee' : 'In your words — one line is plenty'} aria-label={gap.question} />
          )}

          {err && <p className="text-xs text-bad" role="alert">{err}</p>}
          <div className="flex items-center gap-2">
            <Button size="xs" variant="outline" className="hover:bg-brand-subtle" onClick={() => void submit()} disabled={busy} data-testid="habit-gaps-save">Save</Button>
            {gaps.length > 1 && <button type="button" onClick={() => go(1)} disabled={busy} className="min-h-touch px-3 text-sm text-muted hover:text-secondary" data-testid="habit-gaps-next">Next question</button>}
            <span className="ml-auto inline-flex items-center gap-1 text-app-micro text-muted"><Sparkles className="h-3 w-3" aria-hidden="true" /> {gap.hint}</span>
          </div>
          {gaps.length > 1 && (
            <div className="flex items-center justify-center gap-1.5" aria-hidden="true">
              {gaps.map((g, i) => <button key={g.kind} type="button" tabIndex={-1} onClick={() => setIdx(i)} title={g.question} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-brand' : 'w-1.5 bg-soft hover:bg-strong'}`} />)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default HabitGapsCard
